import { Injectable, inject, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Remembers which section of the app a comparison was launched from.
 *
 * Compare is reachable from Rates, Upcoming Rate Changes, Products, the
 * Disclosure surfaces and the Calculator — but every one of them lands on a
 * URL under `/calculator/compare` or `/compare/structural`, which the nav
 * reads as "Calculator". The menu highlight then jumps to Calculator even
 * though the user never left Rates, and the only way back is the browser's
 * back button.
 *
 * This tracks the last non-comparison URL rather than asking each caller to
 * declare an origin. That covers entry points that bypass
 * CompareNavigationService and navigate the router directly (the
 * calculator's own Product Comparison button), and it self-corrects: the
 * origin is whatever page the user was actually on. Persisted to
 * sessionStorage so a refresh on a comparison page keeps the right menu lit.
 */
@Injectable({ providedIn: 'root' })
export class CompareOriginService {
  private static readonly STORAGE_KEY = 'compareOriginUrl';

  /**
   * URL prefixes that count as "inside a comparison". `/calculator/compare`
   * covers both the allocation flow and the income projection page it hands
   * off to, so walking between them never overwrites the origin.
   */
  private static readonly COMPARE_PREFIXES = [
    '/calculator/compare',
    '/compare/structural',
    '/compare-legacy',
  ];

  private readonly router = inject(Router);

  private readonly origin = signal<string | null>(CompareOriginService.read());

  /** Last URL the user was on before entering a comparison surface. */
  readonly originUrl = this.origin.asReadonly();

  constructor() {
    this.record(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(e => this.record(e.urlAfterRedirects));
  }

  /** True when `url` is one of the comparison surfaces. */
  static isCompareSurface(url: string): boolean {
    const path = (url || '').split('?')[0];
    return CompareOriginService.COMPARE_PREFIXES.some(
      p => path === p || path.startsWith(p + '/'),
    );
  }

  private record(url: string): void {
    if (!url || CompareOriginService.isCompareSurface(url)) return;
    if (this.origin() === url) return;
    this.origin.set(url);
    try {
      sessionStorage.setItem(CompareOriginService.STORAGE_KEY, url);
    } catch { /* SSR or storage quota */ }
  }

  private static read(): string | null {
    try {
      return sessionStorage.getItem(CompareOriginService.STORAGE_KEY);
    } catch { return null; }
  }
}
